import { window } from "@swa-ui/browser";

import {
  ADULTS_MAX,
  ADULTS_MIN,
  CHILDREN_MAX,
  CHILDREN_MIN,
  DEFAULT_ADULTS,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
  ESPANOL_HOSTNAME,
  ESPANOL_LOCALE,
  IFRAME_URL_PROD_KEY,
  IFRAME_URL_STAGING_KEY,
  PROD_TIER,
  ROOMS_MAX,
  ROOMS_MIN,
} from "../defines/ppaHotelDefaults";
import { isHotelIFrameDataValid } from "../isHotelIFrameDataValid";

const RESERVED_KEYS = new Set([
  "adults",
  "checkin",
  "checkout",
  "children",
  "cid",
  "currency",
  "destination",
  "layout",
  "rooms",
  "tag",
]);

/**
 * Selects the active PPA iframe base URL from bootstrap data using the supplied environment tier.
 * Returns `iframeUrl.prod` when `environmentTier` is `"prod"`, otherwise returns `iframeUrl.staging`.
 * Trims any trailing slash so URL construction produces a single `/` before the locale segment.
 *
 * Consumers are responsible for supplying the correct tier — no internal hostname detection is
 * performed. Vision SPAs should derive the tier from their own app config or hostname utilities;
 * NextGen SPAs can use `getEnvironmentTier` from `@swa-ui/dotcom`.
 */
export const resolveBaseUrl = (hotelIFrameData, environmentTier) => {
  const iframeUrl = hotelIFrameData?.iframeUrl;
  const key = environmentTier === PROD_TIER ? IFRAME_URL_PROD_KEY : IFRAME_URL_STAGING_KEY;
  const raw = iframeUrl?.[key];

  return raw ? raw.replace(/\/$/, "") : raw;
};

/**
 * Resolves the PPA iframe locale path segment. Returns `"es"` when the page hostname is
 * `espanol.southwest.com` or when the `language` prop is `"es"`, otherwise `"en-us"`.
 *
 * `hostname` defaults to `window.location.hostname`; callers may pass an explicit hostname
 * for testability without mocking `@swa-ui/browser`.
 */
export const resolveLocale = (language, hostname = window.location?.hostname) => {
  const isEspanolHost = hostname === ESPANOL_HOSTNAME;
  const isSpanishLanguage = language === ESPANOL_LOCALE;

  return isEspanolHost || isSpanishLanguage ? ESPANOL_LOCALE : DEFAULT_LOCALE;
};

/**
 * Returns `queryParameters` as an array of `[key, value]` pairs sorted alphabetically by key.
 * Returns an empty array when `queryParameters` is null or not an object. Exported so
 * consumers can build a stable memo/dep key using the same ordering the builder emits.
 */
export const sortEntriesByKey = (queryParameters) =>
  queryParameters && typeof queryParameters === "object"
    ? Object.entries(queryParameters).sort(([a], [b]) => a.localeCompare(b))
    : [];

/**
 * Builds the PPA iframe `src` URL from bootstrap data and search props.
 * Returns `null` when `hotelIFrameData` fails `isHotelIFrameDataValid` — that helper is the
 * single source of truth for config validity so validation logic can't drift out of sync.
 *
 * Pattern: `{baseUrl}/{locale}/widget?{queryParameters}`
 *
 * `options.environmentTier` — required. Selects between `iframeUrl.prod` and `iframeUrl.staging`.
 * Consumers must supply the correct tier explicitly; no internal hostname detection is performed.
 * Vision SPAs should derive this from their own app config or hostname utilities; NextGen SPAs
 * can use `getEnvironmentTier` from `@swa-ui/dotcom`.
 *
 * Optional parameters (destination, checkin, checkout, layout, tag, rooms, children) are omitted
 * when undefined/null or when outside their valid integer range.
 *
 * Callers may pass additional pass-through query parameters via `queryParameters` (e.g. marketing
 * attribution keys like `clk`, `utm_source`). The builder owns the following reserved keys —
 * `cid`, `adults`, `currency`, `destination`, `checkin`, `checkout`, `layout`, `tag`, `rooms`,
 * `children` — and always ignores caller-supplied entries with those names, regardless of whether
 * the top-level option was provided. This prevents a caller from sneaking a reserved key in via
 * `queryParameters` when the corresponding top-level option was omitted.
 */
export const buildIframeSrc = (options) => {
  const {
    adults,
    checkinDate,
    checkoutDate,
    childGuests,
    currency,
    destination,
    environmentTier,
    hotelIFrameData,
    layout,
    locale,
    queryParameters,
    rooms,
    tag,
  } = options;

  const isValid = isHotelIFrameDataValid(hotelIFrameData);
  const baseUrl = isValid ? resolveBaseUrl(hotelIFrameData, environmentTier) : null;
  const params = new URLSearchParams();

  if (isValid) {
    params.set("cid", hotelIFrameData.cid);
    params.set("adults", String(resolveAdults(adults)));
    params.set("currency", currency || DEFAULT_CURRENCY);
    appendIfPresent(params, "destination", destination);
    appendIfPresent(params, "checkin", checkinDate);
    appendIfPresent(params, "checkout", checkoutDate);
    appendIfPresent(params, "layout", layout);
    appendIfPresent(params, "tag", tag);
    appendIfInRange(params, "rooms", rooms, ROOMS_MIN, ROOMS_MAX);
    appendIfInRange(params, "children", childGuests, CHILDREN_MIN, CHILDREN_MAX);
    appendQueryParams(params, queryParameters);
  }

  return isValid ? `${baseUrl}/${locale}/widget?${params.toString()}` : null;
};

function resolveAdults(adults) {
  const isValid = Number.isInteger(adults) && adults >= ADULTS_MIN && adults <= ADULTS_MAX;

  return isValid ? adults : DEFAULT_ADULTS;
}

function appendIfPresent(params, key, value) {
  if (value !== undefined && value !== null && value !== "") {
    params.set(key, String(value));
  }
}

function appendIfInRange(params, key, value, min, max) {
  if (Number.isInteger(value) && value >= min && value <= max) {
    params.set(key, String(value));
  }
}

function appendQueryParams(params, queryParameters) {
  sortEntriesByKey(queryParameters).forEach(([key, value]) => {
    if (!RESERVED_KEYS.has(key)) {
      appendIfPresent(params, key, value);
    }
  });
}
